import React, { useMemo, useState } from 'react';
import {
  Button,
  Link,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableToolbar,
  TableToolbarContent,
  TableToolbarSearch,
} from '@carbon/react';
import {
  ChevronDown,
  ChevronUp,
  DocumentView,
  ViewOff,
} from '@carbon/react/icons';
import { formatDate } from './reconcile-entity-documents';
import { useEntitiesDocuments } from './use-entities-documents';
import type { ReconcileCandidate } from './types';
import styles from './reconcile-source-documents.module.scss';

interface ReconcileSourceDocumentsProps {
  // The selected candidates — every entity in the reconcile selection, not
  // just the survivors, so the examiner sees where each record came from.
  entities: ReconcileCandidate[];
  // Controlled click-to-highlight (mock v2): owned by the tearsheet so the
  // source entity's tile/table row on the left highlights along with every
  // document row from that entity here.
  highlightedEntityId: number | null;
  onHighlight: (entityId: number | null) => void;
  // View-document toggle (mock v25): when provided, the row's View button opens
  // the document in the shared inline Document viewer pane instead of a new tab.
  onViewDocument?: (documentId: number, documentName?: string) => void;
  viewedDocumentId?: number | null;
}

interface DocRow {
  key: string;
  documentId: number;
  entityId: number;
  entity: string;
  party: string;
  address: string;
  doc: string;
  href: string;
  docType: string;
  state: string;
  county: string;
  fileDate: string;
  recordingDate: string;
  effectiveDate: string;
  instrumentDate: string;
}

// Sortable columns only (Dates stacks four values with no single sort key; the
// View column is an action).
type SortKey = keyof Pick<
  DocRow,
  'doc' | 'state' | 'county' | 'docType' | 'entityId' | 'party' | 'address'
>;

const SORT_COLUMNS: Array<{ key: SortKey; label: string }> = [
  { key: 'doc', label: 'Document' },
  { key: 'state', label: 'State' },
  { key: 'county', label: 'County' },
  { key: 'docType', label: 'Document type' },
  { key: 'entityId', label: 'Entity ID' },
  { key: 'party', label: 'Party Details from Document' },
  { key: 'address', label: 'Address' },
];

// Selection-wide "View Documents" disclosure for the reconciliation preview
// panel (mock default-deduplication/v25's per-block source-documents table). A
// ghost button reveals a searchable / sortable / paginated table of the
// documents citing every passed entity, with the columns the mock shows:
// View · Document · Dates (File/Recording/Effective/Instrument) · State ·
// County · Document type · Entity ID · Party Details from Document · Address.
// Clicking a row highlights all rows sharing its source entity; the View button
// opens the document. Per-row address is the entity's address (the documents
// query carries no per-row address — same stand-in the drilldown and mock use).
const ReconcileSourceDocuments: React.FC<ReconcileSourceDocumentsProps> = ({
  entities,
  highlightedEntityId,
  onHighlight,
  onViewDocument,
  viewedDocumentId,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const entityIds = entities.map((e) => e.id);
  const { docsByEntity, loading } = useEntitiesDocuments(entityIds, open);

  const allRows: DocRow[] = useMemo(
    () =>
      entities.flatMap((entity) =>
        (docsByEntity[entity.id] ?? []).map((doc) => ({
          key: `${entity.id}-${doc.id}`,
          documentId: doc.id,
          entityId: entity.id,
          entity: entity.name,
          party:
            doc.partyFromDocument.map((p) => p.text).join('; ') || entity.name,
          address: entity.address || '—',
          doc: doc.documentTitle ?? doc.documentType ?? `Document ${doc.id}`,
          href: doc.href,
          docType: doc.documentType ?? '—',
          state: doc.state ?? '—',
          county: doc.county ?? '—',
          fileDate: formatDate(doc.fileDate),
          recordingDate: formatDate(doc.recordedDate),
          effectiveDate: formatDate(doc.effectiveDate),
          instrumentDate: formatDate(doc.instrumentDate),
        }))
      ),
    [entities, docsByEntity]
  );

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = q
      ? allRows.filter((r) =>
          [
            r.doc,
            r.state,
            r.county,
            r.docType,
            r.entityId,
            r.party,
            r.address,
            r.fileDate,
            r.recordingDate,
            r.effectiveDate,
            r.instrumentDate,
          ]
            .join(' ')
            .toLowerCase()
            .includes(q)
        )
      : allRows;
    if (!sortKey) return filtered;
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...filtered].sort(
      (a, b) =>
        String(a[sortKey]).localeCompare(String(b[sortKey]), undefined, {
          numeric: true,
        }) * dir
    );
  }, [allRows, search, sortKey, sortDir]);

  const pageRows = rows.slice((page - 1) * pageSize, page * pageSize);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setPage(1);
  };
  const sortDirection = (key: SortKey) => {
    if (sortKey !== key) return 'NONE';
    return sortDir === 'asc' ? 'ASC' : 'DESC';
  };

  const allLoaded = entityIds.every((id) => id in docsByEntity);
  // View · Document · Dates · [sortable columns] = 3 leading + the sort columns.
  const columnCount = 3 + SORT_COLUMNS.length - 1;

  return (
    <section className={styles.root}>
      <Button
        kind="ghost"
        size="sm"
        renderIcon={open ? ChevronUp : ChevronDown}
        onClick={() => setOpen((v) => !v)}
      >
        View Documents
      </Button>
      {open && (loading || !allLoaded) && (
        <p className={styles.hint}>Loading documents…</p>
      )}
      {open && !loading && allLoaded && allRows.length === 0 && (
        <p className={styles.hint}>No source documents available.</p>
      )}
      {open && allRows.length > 0 && (
        <div className={styles.shell}>
          <TableToolbar size="sm">
            <TableToolbarContent>
              <TableToolbarSearch
                persistent
                placeholder="Search documents"
                labelText="Search documents"
                onChange={(_evt, value?: string) => {
                  setSearch(value ?? '');
                  setPage(1);
                }}
              />
            </TableToolbarContent>
          </TableToolbar>
          <div className={styles.tableWrap}>
            <Table size="sm" className={styles.table}>
              <TableHead>
                <TableRow>
                  {/* View action — no header label. */}
                  <TableHeader
                    className={styles.viewHeader}
                    aria-label="View"
                  />
                  <TableHeader
                    isSortable
                    isSortHeader={sortKey === 'doc'}
                    sortDirection={sortDirection('doc')}
                    onClick={() => handleSort('doc')}
                  >
                    Document
                  </TableHeader>
                  {/* Dates stacks four values — no single value to sort by. */}
                  <TableHeader>Dates</TableHeader>
                  {SORT_COLUMNS.filter((c) => c.key !== 'doc').map(
                    ({ key, label }) => (
                      <TableHeader
                        key={key}
                        isSortable
                        isSortHeader={sortKey === key}
                        sortDirection={sortDirection(key)}
                        onClick={() => handleSort(key)}
                      >
                        {label}
                      </TableHeader>
                    )
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {pageRows.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={columnCount}
                      className={styles.emptyCell}
                    >
                      No documents match this search.
                    </TableCell>
                  </TableRow>
                ) : (
                  pageRows.map((r) => {
                    const isHighlighted = highlightedEntityId === r.entityId;
                    const toggleHighlight = () => {
                      onHighlight(isHighlighted ? null : r.entityId);
                    };
                    return (
                      <TableRow
                        key={r.key}
                        className={`${styles.row}${
                          isHighlighted ? ` ${styles.rowHighlighted}` : ''
                        }`}
                        aria-selected={isHighlighted || undefined}
                        onClick={toggleHighlight}
                        onKeyDown={(e: React.KeyboardEvent) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            toggleHighlight();
                          }
                        }}
                        tabIndex={0}
                      >
                        <TableCell className={styles.viewCell}>
                          {onViewDocument ? (
                            <Button
                              kind="ghost"
                              size="sm"
                              hasIconOnly
                              renderIcon={
                                viewedDocumentId === r.documentId
                                  ? ViewOff
                                  : DocumentView
                              }
                              iconDescription={
                                viewedDocumentId === r.documentId
                                  ? 'Hide document'
                                  : 'View document'
                              }
                              tooltipPosition="right"
                              onClick={(e: React.MouseEvent) => {
                                e.stopPropagation();
                                onViewDocument(r.documentId, r.doc);
                              }}
                            />
                          ) : (
                            <Button
                              kind="ghost"
                              size="sm"
                              hasIconOnly
                              renderIcon={DocumentView}
                              iconDescription="View document"
                              tooltipPosition="right"
                              href={r.href}
                              target="_blank"
                              rel="noreferrer"
                              onClick={(e: React.MouseEvent) =>
                                e.stopPropagation()
                              }
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <Link
                            href={r.href}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e: React.MouseEvent) =>
                              e.stopPropagation()
                            }
                          >
                            {r.doc}
                          </Link>
                        </TableCell>
                        <TableCell>
                          <div className={styles.dates}>
                            <div>
                              <span className={styles.dateLabel}>
                                File Date:
                              </span>{' '}
                              {r.fileDate}
                            </div>
                            <div>
                              <span className={styles.dateLabel}>
                                Recording Date:
                              </span>{' '}
                              {r.recordingDate}
                            </div>
                            <div>
                              <span className={styles.dateLabel}>
                                Effective Date:
                              </span>{' '}
                              {r.effectiveDate}
                            </div>
                            <div>
                              <span className={styles.dateLabel}>
                                Instrument Date:
                              </span>{' '}
                              {r.instrumentDate}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>{r.state}</TableCell>
                        <TableCell>{r.county}</TableCell>
                        <TableCell>{r.docType}</TableCell>
                        <TableCell>{r.entityId}</TableCell>
                        <TableCell>{r.party}</TableCell>
                        <TableCell>{r.address}</TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
          {rows.length > pageSize && (
            <Pagination
              page={page}
              pageSize={pageSize}
              pageSizes={[25, 50, 100]}
              totalItems={rows.length}
              onChange={({
                page: nextPage,
                pageSize: nextSize,
              }: {
                page: number;
                pageSize: number;
              }) => {
                setPage(nextSize !== pageSize ? 1 : nextPage);
                setPageSize(nextSize);
              }}
            />
          )}
        </div>
      )}
    </section>
  );
};

export default ReconcileSourceDocuments;
